import { Calendar, MapPin, MessageSquare, Sparkles } from "lucide-react";

import { cn } from "@punchup/utils/cn";

interface AudienceSelectionCardProps {
  name: string;
  /** Pre-formatted: "Feb 21, 2026" or "Dec 28 - Jan 5 · 3 shows" */
  dateLabel?: string;
  /** Secondary text below name (used for blast audiences, e.g. "All subscribers + all imported contacts") */
  description?: string;
  /** Optional icon rendered inline before the name (for blast audience icons) */
  icon?: React.ReactNode;
  /** e.g. "Chicago, IL" */
  location?: string | null;
  /** e.g. "Last messaged Feb 11" / "No recent activity" / null */
  messagingActivity?: string | null;
  /** Drives amber warning styling on the messaging activity text */
  recentlyMessaged?: boolean;
  fanCount: number;
  /** Default "fans" — can pass "waitlisted" etc. */
  fanLabel?: string;
  /** Shows sparkle "Suggested" badge next to venue name */
  isSuggested?: boolean;
  /**
   * Muted-grey + pulse on the fan-count number to signal that a new value
   * is being fetched (e.g. after the user changed the audience radius).
   */
  isFanCountLoading?: boolean;
}

export function AudienceSelectionCard({
  name,
  dateLabel,
  description,
  icon,
  location,
  messagingActivity,
  recentlyMessaged,
  fanCount,
  fanLabel = "fans",
  isSuggested,
  isFanCountLoading = false,
}: AudienceSelectionCardProps) {
  return (
    <>
      {/* Left column */}
      <div className="min-w-0 flex-1">
        {/* Line 1: Icon + name + optional Suggested badge */}
        <div className="flex items-center gap-1.5">
          {icon && (
            <span className="inline-flex flex-shrink-0 items-center justify-center rounded-md bg-primary/10 p-1 text-primary">
              {icon}
            </span>
          )}
          <p className="truncate text-sm font-medium text-primary-blue">
            {name}
          </p>
          {isSuggested && (
            <span className="inline-flex flex-shrink-0 items-center gap-0.5 rounded-full bg-primary/10 px-1 py-0.5 text-[10px] font-medium text-primary md:px-1.5">
              <Sparkles className="h-4 w-4" />
              <span className="hidden md:inline">Suggested</span>
            </span>
          )}
        </div>

        {/* Line 2: Description (blast audiences) or date/location metadata (venue cards) */}
        {description && !dateLabel && (
          <p className="mt-0.5 text-xs text-secondary-blue">{description}</p>
        )}

        {dateLabel && (
          <>
            {/* Mobile: date + location inline, messaging activity below */}
            <div className="mt-0.5 flex flex-col gap-0.5 text-xs md:hidden">
              <div className="flex min-w-0 items-center gap-1">
                <span className="shrink-0 text-secondary-blue">
                  {dateLabel}
                </span>
                {location && (
                  <>
                    <span className="shrink-0 text-tertiary-blue">
                      &middot;
                    </span>
                    <span className="truncate text-secondary-blue">
                      {location}
                    </span>
                  </>
                )}
              </div>
              {messagingActivity && (
                <span
                  className={cn(
                    "truncate",
                    recentlyMessaged ? "text-amber-600" : "text-secondary-blue"
                  )}
                >
                  {messagingActivity}
                </span>
              )}
            </div>

            {/* Desktop: inline row with icons and separators */}
            <div className="mt-0.5 hidden min-w-0 items-center gap-1 text-xs md:flex">
              <Calendar className="h-3 w-3 flex-shrink-0 text-tertiary-blue" />
              <span className="shrink-0 text-secondary-blue">{dateLabel}</span>

              {location && (
                <>
                  <span className="shrink-0 text-tertiary-blue">&middot;</span>
                  <MapPin className="h-3 w-3 flex-shrink-0 text-tertiary-blue" />
                  <span className="truncate text-secondary-blue">
                    {location}
                  </span>
                </>
              )}

              {messagingActivity && (
                <>
                  <span className="shrink-0 text-tertiary-blue">&middot;</span>
                  <MessageSquare className="h-3 w-3 flex-shrink-0 text-tertiary-blue" />
                  <span
                    className={cn(
                      "truncate",
                      recentlyMessaged
                        ? "text-amber-600"
                        : "text-secondary-blue"
                    )}
                  >
                    {messagingActivity}
                  </span>
                </>
              )}
            </div>
          </>
        )}
      </div>

      {/* Right column: fan count */}
      <div className="flex flex-shrink-0 items-center gap-2">
        {fanCount === 0 ? (
          <p
            className={cn(
              "text-xs italic text-tertiary-blue",
              isFanCountLoading && "animate-pulse"
            )}
          >
            No fans found
          </p>
        ) : (
          <div className="text-right">
            <p
              className={cn(
                "text-lg font-semibold tabular-nums",
                isFanCountLoading
                  ? "animate-pulse text-tertiary-blue"
                  : "text-primary-blue"
              )}
            >
              {fanCount.toLocaleString()}
            </p>
            <p className="text-xs text-tertiary-blue md:text-[11px]">
              {fanLabel}
            </p>
          </div>
        )}
      </div>
    </>
  );
}
